import { useSelector, useDispatch } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'
import { useTrackingContext } from '../../../../providers/tracking'
import { Goal } from './Goal'
import {
  NEW_ORDERED_HOMEOWNER_GOALS,
  NEW_ORDERED_NON_HOMEOWNER_GOALS,
  NEW_GOAL_MESSAGE_MAP,
  NEW_STEPS,
  QUESTION_GOALS,
  GOALS
} from '../../../../constants/client/onboarding'
import { saveClientAnswer } from '../../../../actions/client'
import { setBuyerOnboardingGoal } from '../../../../actions/buyerOnboarding'
import { selectIsHomeowner } from '../../../../store/selectors/client'
import { useState } from 'react'
import { ButtonGroup, Button, Flex, useToast } from '@homebotapp/hb-react-component-catalog'
import { OnboardingModalTitle } from './OnboardingModalTitle'
import { StepContainer } from './StepContainer'
import { TrackingEventConstants } from '../../../../constants/tracking'

const GOAL_ICON_MAP = {
  [GOALS.GOAL_EXPLORE]: '🔎',
  [GOALS.GOAL_FIRST_HOME]: '🏠',
  [GOALS.GOAL_INVEST]: '🌴',
  [GOALS.GOAL_NEW_HOME]: '💰'
}

const MSG = defineMessages({
  headline: {
    id: 'BuyerOnboarding.goalStep.headline',
    defaultMessage: 'What are you looking to do?'
  },
  skipForNow: {
    id: 'BuyerOnboarding.skipForNow',
    defaultMessage: 'Skip for now'
  },
  next: {
    id: 'BuyerOnboarding.next',
    defaultMessage: 'Next'
  },
  finish: {
    id: 'BuyerOnboarding.finish',
    defaultMessage: 'Finish'
  },
  successMessage: {
    id: 'BuyerOnboardingWizard.successMessage',
    defaultMessage: 'Search profile is 100% complete'
  }
})

interface EnterGoalProps {
  getTotalStepsLeftToComplete: () => number
  handleNavigation: (saveCurrentStepData?: () => void) => void
  hasFavoriteMarkets: boolean
}
export const EnterGoal = ({ getTotalStepsLeftToComplete, handleNavigation, hasFavoriteMarkets }: EnterGoalProps) => {
  const [selectedGoals, setSelectedGoals] = useState([])

  const toast = useToast()
  const intl = useIntl()
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()

  const isHomeowner = useSelector(selectIsHomeowner)

  const saveGoals = goals => {
    trackingClient?.buyerOnboarded({ priority_set: goals })
    dispatch(setBuyerOnboardingGoal(goals))

    goals.forEach(goal => {
      dispatch(
        saveClientAnswer({
          question: QUESTION_GOALS,
          response: {
            value: goal
          }
        })
      )
    })
  }

  const handleSkipForNow = () => {
    handleNavigation()
  }

  const handleNext = () => {
    if (selectedGoals.length > 0) {
      handleNavigation(() => saveGoals(selectedGoals))
      if (getTotalStepsLeftToComplete() === 1 && hasFavoriteMarkets) {
        toast({
          title: intl.formatMessage(MSG.successMessage),
          status: 'success'
        })
      }
    }
  }

  const orderedGoals = isHomeowner ? NEW_ORDERED_HOMEOWNER_GOALS : NEW_ORDERED_NON_HOMEOWNER_GOALS

  return (
    <StepContainer data-qa='enter-goals'>
      <OnboardingModalTitle currentStep={NEW_STEPS.BUYER_GOALS} />
      <Flex flexDirection='column' gap={4}>
        {orderedGoals.map(goal => {
          const goalIcon = GOAL_ICON_MAP[goal]
          const copy = NEW_GOAL_MESSAGE_MAP[goal] || null
          const formattedCopy = intl.formatMessage(copy)

          return (
            <Goal
              goal={goal}
              copy={formattedCopy}
              icon={goalIcon}
              key={goal}
              onClick={() => {
                if (!selectedGoals.includes(goal)) {
                  setSelectedGoals([...selectedGoals, goal])
                } else {
                  setSelectedGoals(selectedGoals.filter(g => g !== goal))
                }
              }}
              selectedGoals={selectedGoals}
            />
          )
        })}
      </Flex>
      <ButtonGroup justifyContent='end' mt={3}>
        <Button
          tracking={{
            guid: 'kRzoMddxc3pcJLLLdVsAwa',
            ui_context: TrackingEventConstants.BUYING_ONBOARDING_WIZARD_SKIP_FOR_NOW,
            descriptor: MSG.skipForNow.defaultMessage
          }}
          onClick={handleSkipForNow}
        >
          {intl.formatMessage(MSG.skipForNow)}
        </Button>
        <Button isDisabled={selectedGoals.length === 0} colorScheme='primary' onClick={handleNext}>
          {getTotalStepsLeftToComplete() === 1 ? intl.formatMessage(MSG.finish) : intl.formatMessage(MSG.next)}
        </Button>
      </ButtonGroup>
    </StepContainer>
  )
}
